/**
 * Declarative Web Push Subscription Manager
 * Uses window.pushManager OR service worker-based push for iOS PWA notifications
 * Compatible with Safari 18.4+, iOS 18.4+, iPadOS 18.4+, macOS 15.5+
 * Registers a service worker to handle push events and display notifications
 *
 * ONLY INITIALIZES ON iOS/iPadOS PWAs (installed to home screen)
 */

import { DeviceDetection } from '../lib/device_detection'
import { ClassLogger } from '../lib/logging/class_logger'
import { get, set, del } from 'idb-keyval'
import { UserActionsLogger } from '../lib/user_actions_logger'
import {
  notification,
  remove_token_notifications,
  subscribe_notifications,
  unsubscribe_all_notifications
} from '../routes'

(function () {
  'use strict'

  const logger = new ClassLogger('DeclarativeWebPush')
  const log = logger.log.bind(logger)
  const warn = logger.warn.bind(logger)
  const error = logger.error.bind(logger)

  function shouldInitialize () {
    if (!DeviceDetection.hasPushManagerInWindow()) {
      return false
    }

    if (!DeviceDetection.isIOS() || !DeviceDetection.isPWA()) {
      return false
    }

    const version = DeviceDetection.getIOSVersion()
    if (!version) {
      return false
    }

    if (version.major > 18) {
      return true
    }

    if (version.major === 18 && version.minor >= 4) {
      return true
    }

    return false
  }
  if (!shouldInitialize()) {
    const version = DeviceDetection.getIOSVersion()
    const versionStr = version
      ? `${version.major}.${version.minor}.${version.patch}`
      : 'unknown'
    log(
      'Not initializing ' +
      `(requires iOS/iPadOS 18.4+ PWA, current: iOS ${versionStr}, ` +
      `PWA: ${DeviceDetection.isPWA()})`
    )
    return
  }

  // Read VAPID public key from meta tag in <head>
  const VAPID_PUBLIC_KEY =
    document.querySelector('meta[name="vapid-public-key"]')?.content || ''
  const SERVICE_WORKER_PATH = '/declarative-web-push-sw.js'

  /**
   * Register the service worker for handling push notifications
   * NOTE: Declarative Web Push does NOT use service workers
   *   it uses window.pushManager
   * This function is kept for fallback compatibility but should not be
   *   needed for iOS 18.4+
   * @returns {Promise<ServiceWorkerRegistration|null>}
   */
  async function registerServiceWorker () {
    if (!('serviceWorker' in navigator)) {
      warn('Service workers not supported')
      return null
    }

    try {
      const registration = await navigator.serviceWorker.register(
        SERVICE_WORKER_PATH,
        { scope: '/' }
      )

      log('Service worker registered successfully', registration)

      await navigator.serviceWorker.ready
      log('Service worker is ready')

      return registration
    } catch (err) {
      error('Service worker registration failed:', err)
      return null
    }
  }

  /**
   * Convert base64 VAPID key to Uint8Array
   * @param {string} base64String - Base64 encoded VAPID public key
   * @returns {Uint8Array}
   */
  function urlBase64ToUint8Array (base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  /**
   * Check if push notifications are supported
   * @returns {boolean}
   */
  function isPushSupported () {
    // Check for window.pushManager (Declarative Web Push)
    if ('pushManager' in window) {
      return true
    }

    // Fallback: check for service worker-based push
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      return true
    }

    return false
  }

  /**
   * Request notification permission from user
   * @returns {Promise<string>} - Permission state: 'granted', 'denied', or 'default'
   */
  async function requestNotificationPermission () {
    if (!('Notification' in window)) {
      warn('This browser does not support notifications')
      return 'denied'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      return 'denied'
    }

    // Request permission
    const permission = await Notification.requestPermission()
    return permission
  }

  /**
   * Subscribe to push notifications using Declarative Web Push or Service Worker
   * @returns {Promise<PushSubscription|null>}
   */
  async function subscribeToPush (ecosystemId) {
    if (!VAPID_PUBLIC_KEY) {
      error(
        'WEB_PUSH_VAPID_PUBLIC_KEY is not configured. ' +
        'Set ENV["WEB_PUSH_VAPID_PUBLIC_KEY"]'
      )
      return null
    }

    try {
      // First, request notification permission
      const permission = await requestNotificationPermission()

      if (permission !== 'granted') {
        log('Notification permission not granted:', permission)
        return null
      }

      // Convert VAPID key to Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)

      let subscription

      // Try Declarative Web Push first (window.pushManager)
      if ('pushManager' in window) {
        log('Using Declarative Web Push (window.pushManager)')
        subscription = await window.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        })
        /* Commented out - Declarative Web Push does not use service workers
        // Fallback to service worker-based push
        else if ('serviceWorker' in navigator) {
          console.log('Using service worker-based push');

          // Ensure service worker is registered and ready
          const registration = await registerServiceWorker();
          if (!registration) {
            console.error('Failed to register service worker');
            return null;
          }

          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey
          });
        }
        */
      } else {
        error('No push notification mechanism available (window.pushManager not found)')
        return null
      }

      log('Push subscription created:', subscription)

      // Send subscription to server and get token_id
      const serverResponse = await sendSubscriptionToServer(subscription, ecosystemId)

      // Store full subscription object in IndexedDB with ecosystem reference
      try {
        const storageKey = DeclarativeWebPushManager.getStorageKey()
        const subscriptionData = subscription.toJSON()

        // Add ecosystem_id to subscription data
        subscriptionData.ecosystem_id = ecosystemId

        // Add notification_token_id from server response
        const responseData = await serverResponse.json()
        if (responseData.token_id) {
          subscriptionData.notification_token_id = responseData.token_id
          log('Stored notification_token_id:', responseData.token_id)
        }

        await set(storageKey, subscriptionData)
        log('Stored subscription in IndexedDB with ecosystem:', ecosystemId)
      } catch (err) {
        error('Failed to store subscription in IndexedDB:', err)
        // Continue anyway - subscription is still active in browser
      }

      return subscription
    } catch (err) {
      error('Failed to subscribe to push notifications:', err)
      return null
    }
  }

  /**
   * Send push subscription to server
   * @param {PushSubscription} subscription
   * @returns {Promise<Response>}
   */
  async function sendSubscriptionToServer (subscription, ecosystemId) {
    const response = await fetch(subscribe_notifications() + `?ecosystem_id=${ecosystemId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        token_type: 'declarative_web_push'
      })
    })

    if (!response.ok) {
      throw new Error(`Failed to send subscription to server: ${response.status}`)
    }

    log('Subscription sent to server successfully')
    return response
  }

  /**
   * Get current push subscription
   * @returns {Promise<PushSubscription|null>}
   */
  async function getCurrentSubscription () {
    try {
      if ('pushManager' in window) {
        return await window.pushManager.getSubscription()
      } else if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        return await registration.pushManager.getSubscription()
      }
      return null
    } catch (err) {
      error('Failed to get current subscription:', err)
      return null
    }
  }

  /**
   * Unsubscribe from push notifications
   * @returns {Promise<boolean>}
   */
  async function unsubscribeFromPush () {
    try {
      const subscription = await getCurrentSubscription()

      if (!subscription) {
        log('No active subscription to unsubscribe from')
        return false
      }

      const successful = await subscription.unsubscribe()

      if (successful) {
        log('Successfully unsubscribed from push notifications')
        // Optionally notify server about unsubscription
      }

      return successful
    } catch (err) {
      error('Failed to unsubscribe:', err)
      return false
    }
  }

  /**
   * Check if user is currently subscribed
   * @returns {Promise<boolean>}
   */
  async function isSubscribed () {
    const subscription = await getCurrentSubscription()
    return subscription !== null
  }

  // Export public API
  window.DeclarativeWebPush = {
    isSupported: isPushSupported,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush,
    getSubscription: getCurrentSubscription,
    isSubscribed,
    requestPermission: requestNotificationPermission,
    registerServiceWorker
  }

  if ('serviceWorker' in navigator) {
    registerServiceWorker().then(() => {
      log('Service worker initialization complete')
    }).catch((err) => {
      error('Service worker initialization failed:', err)
    })
  }

  log('Initialized. Use window.DeclarativeWebPush to manage subscriptions.')
})()

export class DeclarativeWebPushManager {
  static getStorageKey () {
    return 'declarative_web_push_subscription'
  }

  static isSupported () {
    if (!DeviceDetection.hasPushManagerInWindow()) {
      return 'PushManager not available'
    }
    if (!window.DeclarativeWebPush) {
      return 'DeclarativeWebPush API not available'
    }
    return true
  }

  static shouldInitialize () {
    if (!DeviceDetection.hasPushManagerInWindow()) {
      return 'PushManager not available'
    }
    if (!DeviceDetection.isIOS()) {
      return 'Not iOS device'
    }
    if (!DeviceDetection.isPWA()) {
      return 'Not running as PWA'
    }
    const version = DeviceDetection.getIOSVersion()
    if (!version) {
      return 'Could not detect iOS version'
    }
    if (version.major < 18) {
      return `iOS ${version.string} too old (requires 18.4+)`
    }
    if (version.major === 18 && version.minor < 4) {
      return `iOS ${version.string} too old (requires 18.4+)`
    }
    return true
  }

  static async subscribe (ecosystemId) {
    const supported = this.isSupported()
    if (supported !== true) {
      throw new Error(`Declarative Web Push not supported: ${supported}`)
    }
    const subscription = await window.DeclarativeWebPush.subscribe(ecosystemId)
    return subscription
  }

  static async getSubscriptionForEcosystem (ecosystemId) {
    try {
      const subscriptionData = await get(this.getStorageKey())

      // Verify this subscription belongs to the requested ecosystem
      if (subscriptionData && subscriptionData.ecosystem_id === ecosystemId) {
        return subscriptionData
      }

      // No subscription for this ecosystem
      return null
    } catch (err) {
      const logger = new ClassLogger('DeclarativeWebPushManager')
      const error = logger.error.bind(logger)
      error('Error retrieving subscription from IndexedDB:', err)
      return null
    }
  }

  static async getEndpointForEcosystem (ecosystemId) {
    const subscriptionData = await this.getSubscriptionForEcosystem(ecosystemId)
    return subscriptionData?.endpoint || null
  }

  static async hasEndpointForEcosystem (ecosystemId) {
    const endpoint = await this.getEndpointForEcosystem(ecosystemId)
    return !!endpoint
  }

  static async getAllEcosystemSubscriptions () {
    try {
      const subscriptionData = await get(this.getStorageKey())
      if (subscriptionData && subscriptionData.ecosystem_id) {
        // Return array with single subscription for consistency
        return [subscriptionData]
      }
      return []
    } catch (err) {
      const logger = new ClassLogger('DeclarativeWebPushManager')
      const error = logger.error.bind(logger)
      error('Error getting ecosystem subscription:', err)
      return []
    }
  }

  static async unsubscribeFromEcosystem (ecosystemId) {
    const logger = new ClassLogger('DeclarativeWebPushManager')
    const log = logger.log.bind(logger)
    const error = logger.error.bind(logger)

    try {
      // Get stored subscription for this ecosystem
      const storedSubscription = await this.getSubscriptionForEcosystem(ecosystemId)

      if (!storedSubscription || !storedSubscription.endpoint) {
        throw new Error('No subscription found for this ecosystem')
      }

      log('Unsubscribing from ecosystem:', ecosystemId)

      // Delete from backend - prefer ID-based deletion if available
      let response
      if (storedSubscription.notification_token_id) {
        log('Using ID-based deletion with token_id:', storedSubscription.notification_token_id)
        response = await fetch(notification(storedSubscription.notification_token_id), {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
          }
        })
      } else {
        log('Using legacy endpoint-based deletion (no token_id available)')
        response = await fetch(remove_token_notifications(), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
          },
          body: JSON.stringify({
            subscription: { endpoint: storedSubscription.endpoint },
            token_type: 'declarative_web_push',
            ecosystem_id: ecosystemId
          })
        })
      }

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to remove token from backend: ${response.status} ${errorText}`)
      }

      log('Successfully removed token from backend')

      // Remove from IndexedDB (since there's only one subscription per PWA)
      await del(this.getStorageKey())
      log('Removed subscription from IndexedDB')

      // Since PWAs only support one ecosystem, also unsubscribe from browser
      log('Unsubscribing from browser')
      await window.DeclarativeWebPush.unsubscribe()
      log('Browser subscription removed')

      return true
    } catch (err) {
      error('Error unsubscribing from ecosystem:', err)
      throw err
    }
  }

  static async unsubscribeAllEcosystems () {
    const logger = new ClassLogger('DeclarativeWebPushManager')
    const log = logger.log.bind(logger)
    const error = logger.error.bind(logger)

    const supported = this.isSupported()
    if (supported !== true) {
      throw new Error(`Declarative Web Push not supported: ${supported}`)
    }

    try {
      // Remove subscription from IndexedDB (single key)
      await del(this.getStorageKey())
      log('Removed subscription from IndexedDB')

      // Unsubscribe from browser
      const success = await window.DeclarativeWebPush.unsubscribe()
      log('Browser subscription removed')
      return success
    } catch (err) {
      error('Error in unsubscribeAllEcosystems:', err)
      throw err
    }
  }

  static async migrateExistingSubscription (ecosystemId) {
    const logger = new ClassLogger('DeclarativeWebPushManager')
    const log = logger.log.bind(logger)
    const error = logger.error.bind(logger)

    try {
      // Check if we already have this ecosystem in IndexedDB
      const existingSubscription = await this.getSubscriptionForEcosystem(ecosystemId)
      if (existingSubscription) {
        log('Subscription already exists in IndexedDB for ecosystem:', ecosystemId)
        return false
      }

      // Check if browser has a subscription
      const browserSubscription = await window.DeclarativeWebPush.getSubscription()
      if (!browserSubscription) {
        log('No browser subscription to migrate')
        return false
      }

      log('Found browser subscription, storing in IndexedDB for ecosystem:', ecosystemId)

      // Store browser subscription in IndexedDB with ecosystem reference
      const subscriptionData = browserSubscription.toJSON()
      subscriptionData.ecosystem_id = ecosystemId
      await set(this.getStorageKey(), subscriptionData)

      log('Successfully migrated browser subscription to IndexedDB')
      return true
    } catch (err) {
      error('Error migrating existing subscription:', err)
      return false
    }
  }

  static async hasSubscription () {
    const supported = this.isSupported()
    if (supported !== true) {
      const logger = new ClassLogger('DeclarativeWebPushManager')
      const log = logger.log.bind(logger)
      log(`Declarative Web Push not supported: ${supported}`)
      return false
    }
    try {
      const subscription = await window.DeclarativeWebPush.getSubscription()
      return !!subscription
    } catch (err) {
      const logger = new ClassLogger('DeclarativeWebPushManager')
      const error = logger.error.bind(logger)
      error('Error checking subscription:', err)
      return false
    }
  }

  /**
   * Synchronize push notification subscription across IndexedDB, browser, and backend
   *
   * This unified method handles:
   * - Migration from old subscription formats
   * - Endpoint rotation detection and updates
   * - Orphan subscription cleanup
   * - Verification timestamp updates (last_verified_at, last_updated_at)
   *
   * @param {string} ecosystemId - Ecosystem UUID
   * @returns {Promise<Object>} Result object with sync details
   * @returns {boolean} result.synced - Whether sync completed successfully
   * @returns {boolean} result.changed - Whether endpoint/keys rotated
   * @returns {boolean} result.orphanCleaned - Whether orphaned subscription was removed
   */
  static async syncSubscription (ecosystemId) {
    console.log('🔄 [WebPush] Starting syncSubscription() for ecosystem:', ecosystemId)

    try {
      // PHASE 1: Migration - migrate existing browser subscription if needed
      await this.migrateExistingSubscription(ecosystemId)

      // PHASE 2: Get stored subscription from IndexedDB
      const storedSubscription = await this.getSubscriptionForEcosystem(ecosystemId)
      console.log('🔍 [WebPush] Stored subscription:', storedSubscription ? 'present' : 'none')

      if (!storedSubscription) {
        console.log('✅ [WebPush] No stored subscription for this ecosystem')
        return { synced: true, changed: false, orphanCleaned: false }
      }

      // PHASE 3: Check for old format (missing notification_token_id)
      if (!storedSubscription.notification_token_id) {
        console.log('🧹 [WebPush] Old format detected (no token ID), cleaning up')
        await del(this.getStorageKey())
        console.log('🧹 [WebPush] Cleaned up legacy IndexedDB record')

        // Log orphan cleanup
        await UserActionsLogger.logWebPush('Orphan subscription cleaned (legacy format)', {
          reason: 'legacy_format_no_token_id',
          ecosystemId
        })

        return { synced: true, changed: false, orphanCleaned: true }
      }

      // PHASE 4: Get current browser subscription
      const browserSubscription = await window.DeclarativeWebPush.getSubscription()
      console.log('🔍 [WebPush] Browser subscription:', browserSubscription ? 'present' : 'none')

      if (!browserSubscription) {
        console.log('🧹 [WebPush] Browser subscription missing, cleaning IndexedDB record')
        await del(this.getStorageKey())

        // Log orphan cleanup
        await UserActionsLogger.logWebPush('Orphan subscription cleaned (browser revoked)', {
          reason: 'browser_missing',
          ecosystemId,
          tokenId: storedSubscription.notification_token_id
        })

        return { synced: true, changed: false, orphanCleaned: true }
      }

      // Extract current endpoint and keys from browser
      const currentEndpoint = browserSubscription.endpoint
      const currentKeys = browserSubscription.toJSON().keys

      if (!currentKeys || !currentKeys.p256dh || !currentKeys.auth) {
        console.error('❌ [WebPush] Browser subscription missing encryption keys')
        return { synced: false, changed: false, orphanCleaned: false }
      }

      // PHASE 5: PATCH backend with browser's current data
      console.log('🔍 [WebPush] PATCHing backend with browser\'s current endpoint/keys')
      console.log('🔍 [WebPush] Token ID:', storedSubscription.notification_token_id)
      console.log('🔍 [WebPush] Endpoint:', currentEndpoint.substring(0, 50) + '...')

      const response = await fetch(notification(storedSubscription.notification_token_id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          endpoint: currentEndpoint,
          p256dh: currentKeys.p256dh,
          auth: currentKeys.auth,
          token_type: 'declarative_web_push'
        })
      })

      console.log('🔍 [WebPush] Backend response status:', response.status)

      // Handle orphan cases (404 = deleted, 403 = wrong user)
      if (response.status === 404) {
        console.log('🔄 [WebPush] Token not found (404), attempting to recreate on backend')

        // Get stored subscription data from IndexedDB
        const storedData = await get(this.getStorageKey())

        if (!storedData || !storedData.endpoint || !storedData.keys?.p256dh || !storedData.keys?.auth) {
          console.log('🧹 [WebPush] Missing data for recreation, cleaning up')
          await del(this.getStorageKey())
          await UserActionsLogger.logWebPush('Orphan subscription cleaned (no recovery data)', {
            reason: 'backend_404_no_data',
            ecosystemId,
            tokenId: storedSubscription.notification_token_id
          })
          return { synced: true, changed: false, orphanCleaned: true }
        }

        // Attempt to recreate token on backend
        try {
          console.log('🔄 [WebPush] Recreating subscription on backend...')
          const recreateResponse = await fetch(subscribe_notifications(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
            },
            body: JSON.stringify({
              subscription: {
                endpoint: storedData.endpoint,
                keys: {
                  p256dh: storedData.keys.p256dh,
                  auth: storedData.keys.auth
                }
              },
              token_type: 'declarative_web_push',
              ecosystem_id: ecosystemId
            })
          })

          if (recreateResponse.ok) {
            const data = await recreateResponse.json()
            console.log('✅ [WebPush] Subscription successfully recreated:', data.token_id)

            // Update IndexedDB with new token ID
            storedData.notification_token_id = data.token_id
            await set(this.getStorageKey(), storedData)

            // Log successful adoption
            await UserActionsLogger.logWebPush('Orphan subscription adopted 🏠', {
              reason: 'backend_404_adopted',
              ecosystemId,
              oldTokenId: storedSubscription.notification_token_id,
              newTokenId: data.token_id,
              endpointPreview: storedData.endpoint.substring(0, 50)
            })

            return { synced: true, changed: false, orphanCleaned: false, adopted: true }
          } else {
            console.error('❌ [WebPush] Failed to recreate subscription:', recreateResponse.status)
            throw new Error(`Recreation failed with status ${recreateResponse.status}`)
          }
        } catch (err) {
          console.error('❌ [WebPush] Error recreating subscription:', err)
          // Fall back to cleanup
          await del(this.getStorageKey())
          await UserActionsLogger.logWebPush('Orphan subscription cleaned (recreation failed)', {
            reason: 'backend_404_recreation_failed',
            ecosystemId,
            tokenId: storedSubscription.notification_token_id,
            error: err.message
          })
          return { synced: true, changed: false, orphanCleaned: true }
        }
      }

      if (response.status === 403) {
        console.log('🧹 [WebPush] Access denied (403), cleaning up')
        await del(this.getStorageKey())
        console.log('🧹 [WebPush] Cleaned up unauthorized IndexedDB record')

        // Log orphan cleanup - access denied
        await UserActionsLogger.logWebPush('Orphan subscription cleaned (wrong user)', {
          reason: 'backend_403_access_denied',
          ecosystemId,
          tokenId: storedSubscription.notification_token_id
        })

        return { synced: true, changed: false, orphanCleaned: true }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ [WebPush] Backend PATCH failed:', response.status, errorData)
        return { synced: false, changed: false, orphanCleaned: false }
      }

      // PHASE 6: Parse backend response
      const data = await response.json()
      console.log('✅ [WebPush] Backend response:', data)

      if (data.changed) {
        console.log('🔄 [WebPush] Token updated (endpoint/keys rotated):', data.message)

        // Log endpoint rotation - CRITICAL! (Homepage sync)
        await UserActionsLogger.logWebPush('Endpoint rotated (homepage sync) ❗', {
          ecosystemId,
          tokenId: storedSubscription.notification_token_id,
          oldEndpoint: storedSubscription.endpoint?.substring(0, 50),
          newEndpoint: currentEndpoint.substring(0, 50),
          source: 'homepage_sync',
          message: data.message
        })
      } else {
        console.log('✅ [WebPush] Token verified (no rotation):', data.message)
        // Don't log successful verification (per requirements - only anomalies)
      }

      // PHASE 7: Sync IndexedDB with browser subscription
      console.log('🔍 [WebPush] Syncing IndexedDB with browser subscription')
      const updatedSubscriptionData = browserSubscription.toJSON()
      updatedSubscriptionData.notification_token_id = storedSubscription.notification_token_id
      updatedSubscriptionData.ecosystem_id = ecosystemId
      await set(this.getStorageKey(), updatedSubscriptionData)
      console.log('✅ [WebPush] IndexedDB updated')

      // PHASE 8: Return detailed result
      const result = {
        synced: true,
        changed: data.changed,
        orphanCleaned: false
      }
      console.log('✅ [WebPush] Sync complete:', result)
      return result
    } catch (err) {
      console.error('❌ [WebPush] Error in syncSubscription():', err)

      // Log the error
      await UserActionsLogger.logWebPush('Sync error', {
        ecosystemId,
        error: err.message,
        errorType: err.name
      }).catch(() => {
        // Silent fail if logging fails - don't block the sync operation
      })

      return { synced: false, changed: false, orphanCleaned: false }
    }
  }

  static async unsubscribeAll () {
    const response = await fetch(unsubscribe_all_notifications(), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })
    if (!response.ok) {
      throw new Error('Failed to unsubscribe all devices')
    }
    const data = await response.json()
    return data.message
  }
}
